/**
 * Перевод кодов ФФД карточки собственного товара в строки кассового агента — ОДНА таблица на всё клиентское приложение.
 * Источник — `docs/plan/resource-platform/CASH-CONTRACTS.md` §1 (договор RP-OWN6); тест `fiscalCodes.test.ts` сверяет эту
 * таблицу с договором и с `OwnFiscal.cs`, поэтому правится она только вместе с договором.
 *
 * Правила: код вне таблицы НИКОГДА не заменяется ближайшим («товар» по умолчанию запрещён); `null` — «не задан», и продажа
 * такой строки блокируется до оплаты. Данные, а не ветвления: добавить значение — добавить строку таблицы.
 */

/**
 * Предмет расчёта: тег 1212 → `paymentObject` агента. Коды 1..15; код 12 уходит как `composite` (`pay` — второе имя того же кода 12).
 * Код 27 (`fundsIssuance`) и 16..33 агент не поддерживает: строка блокируется до оплаты.
 */
export const PAYMENT_OBJECT_BY_CODE: Readonly<Record<number, string>> = {
  1: 'commodity',
  2: 'excise',
  3: 'job',
  4: 'service',
  5: 'gamblingBet',
  6: 'gamblingPrize',
  7: 'lottery',
  8: 'lotteryPrize',
  9: 'intellectualActivity',
  10: 'payment',
  11: 'agentCommission',
  12: 'composite',
  13: 'another',
  14: 'proprietaryLaw',
  15: 'nonOperatingIncome',
}

/** Способ расчёта: тег 1214 → `paymentMethod` агента. */
export const PAYMENT_METHOD_BY_CODE: Readonly<Record<number, string>> = {
  1: 'fullPrepayment',
  2: 'prepayment',
  3: 'advance',
  4: 'fullPayment',
  5: 'partialPayment',
  6: 'credit',
  7: 'creditPayment',
}

/** Границы кодов карточки: предмет 1..33 (тег 1212), способ 1..7 (тег 1214). */
export const PAYMENT_SUBJECT_RANGE = { min: 1, max: 33 } as const
export const PAYMENT_METHOD_RANGE = { min: 1, max: 7 } as const

const inRange = (code: number, range: { min: number; max: number }): boolean => Number.isInteger(code) && code >= range.min && code <= range.max

/** Строка агента для предмета расчёта; `null` — кода нет в таблице (не задан, вне диапазона или не поддержан). */
export function paymentObjectOf(code: number | null): string | null {
  if (code === null || !Number.isInteger(code)) return null
  return PAYMENT_OBJECT_BY_CODE[code] ?? null
}

/** Строка агента для способа расчёта; `null` — кода нет в таблице. */
export function paymentMethodOf(code: number | null): string | null {
  if (code === null || !Number.isInteger(code)) return null
  return PAYMENT_METHOD_BY_CODE[code] ?? null
}

/** Код предмета расчёта, который карточка вообще может хранить (1..33), но агент его не знает: блокировка «не поддержан». */
export const isKnownPaymentSubjectCode = (code: number): boolean => inRange(code, PAYMENT_SUBJECT_RANGE)
export const isKnownPaymentMethodCode = (code: number): boolean => inRange(code, PAYMENT_METHOD_RANGE)
